import useIsMounted from "../../../../../../hooks/use-is-mounted";
import React, {useCallback} from "react";
import {createDateRangeInitialValues, createDateRangeSchema, DateRangeFormSection, prepareDateRangeFilters} from "../form-sections/date-range";
import Api from "../../../../../../../core/services/api/api";
import DateUtils from "../../../../../../../core/services/date-utils";
import {formatMoney} from "../../../../../../../core/services/utils/utils";
import {Card, MenuItem, Typography} from "@mui/material";
import Form, {MuiRFF, Yup} from "../../../../../../components/form";
import Grid2 from "@mui/material/Unstable_Grid2";
import Button from "../../../../../../components/base/button/button";
import {PriceMatrixSources, PriceMatrixTypes} from "../../../../../../components/dialogs/upsert-price-matrix";
import useCachedPriceLevelsDictionaryController from "../../../../../../hooks/use-price-level-dictionary";
import PriceMatrixExceptionalCustomersAutocomplete from "../../../../../../components/text-fields/autocomplete/customer-autocomplete/price-matrix-exceptional-customers";
import useIndustry from "../../../../../../hooks/use-industry";
import ExportingProgress from "../exporting-progress";

const targetTypes = {
    entireSystem: "entireSystem",
    exceptionalCustomers: "exceptionalCustomers",
}

const activationStatus = {
    all: "all",
    active: "active",
    inactive: "inactive",
}

export const ExportDataViewPriceMatricesForm = ({exportReport, exporting, progress}) => {
    const isMounted = useIsMounted();
    const {priceLevelDict} = useCachedPriceLevelsDictionaryController({loadDict: true});
    const {industries, loadingIndustries} = useIndustry({noCategories: true});

    const schema = React.useMemo(() => {
        const dateRangeSchema = createDateRangeSchema();
        return Yup.object().shape({
            ...dateRangeSchema,
            targetType: Yup.string().required('Target Type is required'),
            exceptionalCustomers: Yup.array(Yup.mixed().nullable().optional())
                .when('targetType', {
                    is: targetTypes.exceptionalCustomers,
                    then: (schema) => schema.min(1, 'At least one customer must be selected').required('Customers are required').nullable(),
                    otherwise: (schema) => schema.optional().nullable(),
                })
            ,
            activationStatus: Yup.string().required('Activation Status is required'),
        })
    }, []);

    const initialValues = React.useMemo(() => {
        const dateRangeInitialValues = createDateRangeInitialValues();
        return {
            ...dateRangeInitialValues,
            targetType: targetTypes.entireSystem,
            exceptionalCustomers: [],
            activationStatus: activationStatus.all,
        }
    }, []);

    const validate = React.useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = React.useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    /**
     * Fetches the data for export
     * @type {(function(*, *): Promise<{hasNext: boolean, data: *[]} | undefined>)}
     */
    const fetchAndPrepareData = useCallback(async (filters) => {
        let priceMatrices = [];
        // filter by target type
        switch (filters.targetType) {
            case targetTypes.entireSystem: {
                const response = await Api.getSystemPriceMatrices();
                if (!isMounted())
                    return;
                if (response?.resultFlag) {
                    priceMatrices.push(...response.data ?? []);
                }
                break;
            }
            case targetTypes.exceptionalCustomers: {
                const customerIds = Array.from(new Set(filters.exceptionalCustomers.map(e => e.id)));
                const responses = await Promise.all(customerIds.map(id => Api.getCustomerPriceMatrices(id)));
                if (!isMounted())
                    return;
                for (const response1 of responses) {
                    if (response1?.resultFlag) {
                        priceMatrices.push(...response1.data ?? []);
                    }
                }
            }
                break;
            default:
                break;
        }
        // filter by activation status
        switch (filters.activationStatus) {
            default:
            case activationStatus.all: {
                break;
            }
            case activationStatus.active: {
                priceMatrices = priceMatrices.filter((matrix) => (
                    (!matrix.endDate || DateUtils.serverToLocal(matrix.endDate).isSameOrAfter(DateUtils.localNow(), 'day')) &&
                    (!matrix.startDate || DateUtils.serverToLocal(matrix.startDate).isSameOrBefore(DateUtils.localNow(), 'day'))
                ));
            }
        }
        // filter by date range
        priceMatrices = priceMatrices.filter((matrix) => {
            const matrixStart = matrix.startDate ? DateUtils.serverToLocal(matrix.startDate) : null;
            const matrixEnd = matrix.endDate ? DateUtils.serverToLocal(matrix.endDate) : null;
            const filterStart = filters.startDate ? DateUtils.localToServer(filters.startDate) : null;
            const filterEnd = filters.endDate ? DateUtils.localToServer(filters.endDate) : null;

            if (matrixStart && filterEnd && matrixStart.isAfter(filterEnd, 'day')) {
                return false;
            }
            // noinspection RedundantIfStatementJS
            if (matrixEnd && filterStart && matrixEnd.isBefore(filterStart, 'day')) {
                return false;
            }
            return true;
        });

        const transformedMatrices = priceMatrices.map(matrix => {
            const type = matrix.part !== null
                ? PriceMatrixTypes.part
                : matrix.partCategory !== null
                    ? PriceMatrixTypes.category
                    : matrix.priceLevel !== null
                        ? PriceMatrixTypes.priceLevel
                        : PriceMatrixTypes.general;
            let target;
            switch (type.id) {
                case PriceMatrixTypes.part.id:
                    target = matrix.part?.partNo;
                    break;
                case PriceMatrixTypes.category.id:
                    target = matrix.partCategory?.title;
                    break;
                case PriceMatrixTypes.priceLevel.id:
                    target = priceLevelDict?.find(p => p.priceLevelId === matrix.priceLevel?.id)?.title ?? matrix.priceLevel?.title;
                    break;
                case PriceMatrixTypes.general.id:
                default:
                    target = PriceMatrixTypes.general.title;
                    break;
            }
            let change = matrix.isPercentage ? `${matrix.value}%` : formatMoney(matrix.value);
            if (matrix.isFixedValue) {
                change = change.concat(' (Fixed)');
            }
            const basedOn = matrix.isBasedOnCost ? PriceMatrixSources.cost.title : PriceMatrixSources.finalPrice.title;
            return {change, basedOn, type, target, matrix};
        });

        const allCategoryIds = Array.from(new Set(transformedMatrices.filter(m => m.type.id === PriceMatrixTypes.category.id).map(m => m.matrix?.partCategory?.id)));
        const categoryIndustryMap = {};
        const response = await Api.getCategoryIndustryIds({ids: allCategoryIds})
        if (response?.resultFlag) {
            for (const item of response.data ?? []) {
                categoryIndustryMap[item.id] = industries.find(ind => ind.id === item.industryId);
            }
        }

        return transformedMatrices.map(({type, target, basedOn, change, matrix}, index) => {
            return ({
                "": index + 1,
                "Type": type?.title,
                ...(type?.id === PriceMatrixTypes.category.id && {
                    "Industry": categoryIndustryMap[matrix.partCategory?.id]?.title ?? "",
                }),
                "Target": target,
                "Change": change,
                "Based on": basedOn,
                "Start Date": matrix.startDate ? DateUtils.serverToLocal(matrix.startDate).format(DateUtils.withoutOffsetFormat) : "",
                "End Date": matrix.endDate ? DateUtils.serverToLocal(matrix.endDate).format(DateUtils.withoutOffsetFormat) : "",
                ...(filters.targetType === targetTypes.exceptionalCustomers && {
                    "Customer Name": matrix.customer?.name ?? "",
                    "Customer Code": `="${matrix.customer?.code ?? ""}"`,
                }),
            });
        });
    }, [isMounted, priceLevelDict, industries])

    /**
     * Exports the report based on the selected
     * @type {(function(*, *): Promise<void>)}
     */
    const submitForm = useCallback(async (values) => {
        const filters = {
            ...values,
            ...prepareDateRangeFilters(values),
        }
        await exportReport(async () => {
            const data = await fetchAndPrepareData(filters);
            let title = "Price Matrices";
            if (filters.targetType === targetTypes.exceptionalCustomers) {
                const customerNames = filters.exceptionalCustomers.map(c => c.name).join(", ");
                title = `${title} - Exceptional Customers (${customerNames})`
            } else {
                title = `${title} - General`
            }
            if (filters.activationStatus === activationStatus.active) {
                title = `${title} - Active`
            } else if (filters.activationStatus === activationStatus.inactive) {
                title = `${title} - Inactive`
            }
            return [data, title];
        });
    }, [exportReport, fetchAndPrepareData])


    const targetTypeOptions = React.useMemo(() => [
        {label: "General Price matrices", value: targetTypes.entireSystem},
        {label: "Exceptional Customers' price matrices", value: targetTypes.exceptionalCustomers},
    ], [])

    const activationStatusOptions = React.useMemo(() => [
        {label: "All", value: activationStatus.all},
        {label: "Active", value: activationStatus.active},
        {label: "Inactive", value: activationStatus.inactive},
    ], [])

    return (
        <>
            <Card variant={'outlined'} sx={{padding: 3, marginTop: 2}}>
                <Form
                    onSubmit={submitForm}
                    validate={validate}
                    initialValues={initialValues}
                    formProps={{style: {width: '100%'}}}
                >
                    {({values}) => {
                        return (
                            <>
                                <Grid2 container spacing={3} justifyContent={'center'}>
                                    <Grid2 xs={12} sx={{display: 'flex'}}>
                                        <Typography variant={'subtitle1'} align={'center'}>
                                            Select Filters for Price Matrices Report
                                        </Typography>
                                    </Grid2>
                                    <DateRangeFormSection required={required}/>
                                    <Grid2 xs={12} md={6}>
                                        <MuiRFF.TextField
                                            select
                                            name={'targetType'}
                                            label={'Target Type'}
                                            fullWidth
                                            required
                                            size={'small'}
                                        >
                                            {
                                                targetTypeOptions.map(e => (
                                                    <MenuItem key={e.value} value={e.value}>
                                                        {e.label}
                                                    </MenuItem>
                                                ))
                                            }
                                        </MuiRFF.TextField>
                                    </Grid2>
                                    <Grid2 xs={12} md={6}>
                                        <MuiRFF.TextField
                                            select
                                            name={'activationStatus'}
                                            label={'Activation Status'}
                                            fullWidth
                                            required
                                            size={'small'}
                                        >
                                            {
                                                activationStatusOptions.map(e => (
                                                    <MenuItem key={e.value} value={e.value}>
                                                        {e.label}
                                                    </MenuItem>
                                                ))
                                            }
                                        </MuiRFF.TextField>
                                    </Grid2>
                                    {
                                        values.targetType === targetTypes.exceptionalCustomers && (
                                            <Grid2 xs={12}>
                                                <PriceMatrixExceptionalCustomersAutocomplete
                                                    multiple
                                                    name="exceptionalCustomers"
                                                    label="Customers"
                                                    placeholder="Search for customer..."
                                                    fullWidth
                                                    required={required.exceptionalCustomers}
                                                    form
                                                    disabled={exporting}
                                                    textFieldProps={{size: 'small'}}
                                                />
                                            </Grid2>
                                        )
                                    }
                                    <Grid2 xs={12} sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBlockStart: 2}}>
                                        <Button size={"small"} disabled={exporting || loadingIndustries} variant={'outlined'} color={'primary'} type={'submit'} sx={{minWidth: 200}}>
                                            {
                                                loadingIndustries
                                                    ? "Loading Industries Before Exporting..."
                                                    : exporting ? "Exporting..." : "Export"
                                            }
                                        </Button>
                                    </Grid2>
                                </Grid2>
                            </>
                        );
                    }}
                </Form>
                {
                    exporting &&
                    <ExportingProgress progress={progress}/>
                }
            </Card>
        </>
    );
}